import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { HiPlus, HiSearch, HiPencil, HiTrash } from 'react-icons/hi';
import { Button, Input, Select, Card, CardHeader, Table, Pagination, Modal } from '../components/ui';
import { listCustomers, createCustomer, updateCustomer, deleteCustomer, getLinkableCustomerUsers } from '../services/customers.api';
import { listPartners } from '../services/partners.api';
import { useAuthStore } from '../store/authStore';
import { formatDate } from '../utils/helpers';
import { getErrorMessage } from '../services/api';
import type { Customer } from '../types';

export function Customers() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const canCreate = user?.role === 'ADMIN' || user?.role === 'OWNER';
  const canEdit = user?.role === 'ADMIN' || user?.role === 'OWNER' || user?.role === 'INVOICE_TEAM' || user?.role === 'PARTNER';
  const canDelete = user?.role === 'ADMIN' || user?.role === 'OWNER';
  const canAssignPartner = user?.role === 'ADMIN' || user?.role === 'OWNER' || user?.role === 'INVOICE_TEAM';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [address, setAddress] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [userId, setUserId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['customers', { page, search }],
    queryFn: () => listCustomers({ page, search }),
  });

  const { data: partnersData } = useQuery({
    queryKey: ['partners', { limit: 100 }],
    queryFn: () => listPartners({ limit: 100 }),
    enabled: canAssignPartner,
  });

  // Selectable users (role Customer, not yet linked) — a customer is created from one of these.
  const { data: linkableUsers } = useQuery({
    queryKey: ['linkableCustomerUsers'],
    queryFn: getLinkableCustomerUsers,
    enabled: isCreateOpen && canCreate,
  });

  const partnerOptions = [
    { value: '', label: 'No Partner' },
    ...(partnersData?.data?.map((p) => ({ value: p.id, label: p.name })) || []),
  ];

  const createMutation = useMutation({
    mutationFn: () => createCustomer({ userId, name, phone, company, address, partnerId: partnerId || undefined }),
    onSuccess: () => {
      toast.success('Customer created successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['linkableCustomerUsers'] });
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: () => updateCustomer(selectedCustomer!.id, { name, phone, company, address, partnerId: partnerId || null }),
    onSuccess: () => {
      toast.success('Customer updated successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setIsEditOpen(false);
      setSelectedCustomer(null);
      resetForm();
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCustomer(selectedCustomer!.id),
    onSuccess: () => {
      toast.success('Customer deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setIsDeleteOpen(false);
      setSelectedCustomer(null);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const resetForm = () => {
    setName('');
    setEmail('');
    setPhone('');
    setCompany('');
    setAddress('');
    setPartnerId('');
    setUserId('');
  };

  const openEditModal = (customer: Customer, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCustomer(customer);
    setName(customer.name);
    setEmail(customer.email);
    setPhone(customer.phone || '');
    setCompany(customer.company || '');
    setAddress(customer.address || '');
    setPartnerId(customer.partner?.id || '');
    setIsEditOpen(true);
  };

  const openDeleteModal = (customer: Customer, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCustomer(customer);
    setIsDeleteOpen(true);
  };

  const columns = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'company', header: 'Company', render: (c: Customer) => c.company || '-' },
    {
      key: 'partner',
      header: 'Partner',
      render: (c: Customer) => c.partner?.name || '-',
    },
    {
      key: 'licenses',
      header: 'Licenses',
      render: (c: Customer) => c._count?.licenses ?? 0,
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (c: Customer) => formatDate(c.createdAt),
    },
    ...(canEdit || canDelete
      ? [
          {
            key: 'actions',
            header: 'Actions',
            render: (c: Customer) => (
              <div className="flex gap-2">
                {canEdit && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={(e) => openEditModal(c, e)}
                  >
                    <HiPencil className="w-4 h-4" />
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={(e) => openDeleteModal(c, e)}
                  >
                    <HiTrash className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <CardHeader
        title="Customers"
        description="Manage customer accounts"
        action={
          canCreate && (
            <Button onClick={() => setIsCreateOpen(true)}>
              <HiPlus className="w-4 h-4 mr-2" />
              New Customer
            </Button>
          )
        }
      />

      <Card className="mb-6" padding={false}>
        <div className="p-4 border-b border-gray-200">
          <div className="relative max-w-md">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search customers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={data?.data || []}
          keyExtractor={(customer) => customer.id}
          isLoading={isLoading}
          emptyMessage="No customers found"
          onRowClick={(customer) => navigate(`/customers/${customer.id}`)}
        />

        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            total={data.total}
            limit={data.limit}
            onPageChange={setPage}
          />
        )}
      </Card>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Create New Customer"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="space-y-4"
        >
          {linkableUsers && linkableUsers.length === 0 ? (
            <div className="text-sm bg-amber-50 text-amber-700 rounded-lg px-3 py-2">
              Kein verknüpfbarer Benutzer vorhanden. Bitte zuerst unter <strong>Users</strong> einen Benutzer mit
              Rolle <strong>Customer</strong> anlegen (E-Mail + Rolle), dann hier auswählen.
            </div>
          ) : (
            <Select
              label="Benutzer (Rolle Customer)"
              options={[
                { value: '', label: 'Benutzer auswählen…' },
                ...(linkableUsers?.map((u) => ({
                  value: u.id,
                  label: `${u.email} (${u.firstName} ${u.lastName})`,
                })) || []),
              ]}
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                const sel = linkableUsers?.find((u) => u.id === e.target.value);
                setEmail(sel?.email || '');
                if (sel && !name) setName(`${sel.firstName} ${sel.lastName}`.trim());
              }}
              required
            />
          )}
          {email && <p className="-mt-2 text-xs text-gray-500">E-Mail: {email}</p>}
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Input
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Input
            label="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
          <Input
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          {canAssignPartner && (
            <Select
              label="Partner"
              options={partnerOptions}
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
            />
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending} disabled={!userId}>
              Create Customer
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedCustomer(null);
          resetForm();
        }}
        title="Edit Customer"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateMutation.mutate();
          }}
          className="space-y-4"
        >
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <Input
            type="email"
            label="Email (vom verknüpften Benutzer)"
            value={email}
            disabled
            className="bg-gray-50 text-gray-500"
          />
          <Input
            label="Phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <Input
            label="Company"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
          <Input
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          {canAssignPartner && (
            <Select
              label="Partner"
              options={partnerOptions}
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
            />
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsEditOpen(false);
                setSelectedCustomer(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={updateMutation.isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedCustomer(null);
        }}
        title="Delete Customer"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete <strong>{selectedCustomer?.name}</strong>? This action
          will deactivate the customer account.
        </p>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setIsDeleteOpen(false);
              setSelectedCustomer(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteMutation.mutate()}
            isLoading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
